<template>
    <div class="candidate-container" :data-index="candidate.id" :class="{ selected: active }">
        <img :src="thumbnail" @load="revokeObjectURL(thumbnail)" @click="select(candidate)"
             :data-index="candidate.id"/>
        <div class="label-container">
            <v-text-field
                v-if="renamingFile"
                @blur="endFileRenaming"
                @keydown="renameFile"
                v-model="renamingFileName"
                autofocus
                label="File Name"
                dark
                flat
                solo-inverted
                hide-details
            ></v-text-field>
            <div v-else class="label-container-inner">
                <span  class="name">{{ nicename(candidate.name) }}</span>
            </div>

            <v-progress-circular v-if="applying" indeterminate color="var(--primary)" size="14"
                                 width="2"></v-progress-circular>
        </div>

        <v-menu offset-y :content-class="'file-options-container'">
            <template v-slot:activator="{ on }">
                <i class="material-icons" v-on="on">more_vert</i>
            </template>
            <v-list>
                <v-list-item @click="downloadLUT(candidate)" :disabled="!canExportLUT || blockLoading">
                    <i class="material-icons">save_alt</i>
                    <v-list-item-title>Download a LUT</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadImage(candidate)" :disabled="!canExportLUT || blockLoading">
                    <i class="material-icons">photo</i>
                    <v-list-item-title>Save Image</v-list-item-title>
                </v-list-item>
                <v-list-item @click="select(candidate)" :disabled="blockLoading">
                    <i class="material-icons">perm_media</i>
                    <v-list-item-title>Apply Grade</v-list-item-title>
                </v-list-item>
                <v-list-item @click="startFileRenaming(candidate)" :disabled="!canRenameAsset">
                    <i class="material-icons">create</i>
                    <v-list-item-title>Rename</v-list-item-title>
                </v-list-item>
                <v-list-item @click="remove" :disabled="!canDeleteAsset">
                    <v-icon class="material-icons">mdi-delete</v-icon>
                    <v-list-item-title>Remove from My Selection</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import CanMixin from "@/mixins/CanMixin";
import FilenameMixin from "@/mixins/Filename";
import {candidatesStore} from "@/services/indexedDB.service";
import download from "downloadjs";

export default {
    mixins: [CanMixin, FilenameMixin],

    data() {
        return {
            applying: false,
            renamingFile: false,
            renamingFileName: "",
            thumbnail: null,
        };
    },

    props: {
        candidate: Object,
    },

    created() {
        this.thumbnail = URL.createObjectURL(this.candidate.thumbnailBlob);
    },

    computed: {
        ...mapState('File/Candidates', ['tags']),
        ...mapGetters('User', ['isGuest']),
        ...mapState('App', ['blockLoading']),

        active() {
            if (this.$store.state.Projects.candidate == this.candidate.id) return true;
            else return false;
        },
    },

    methods: {
        ...mapActions("Helpers", ["revokeObjectURL"]),

        nicename(name) {
            return this.stripTimestampAndExtension(name);
        },

        async select(candidate) {
            // showing the progress before thread is blocked by applying candidate
            await new Promise(resolve => {
                this.applying = true;
                this.$store.dispatch("Loader/showProgressBar", "indeterminate");

                setTimeout(() => {
                    resolve();
                }, 50);
            });

            this.$store.dispatch("Projects/setActiveCandidate", candidate.id);

            if (candidate.rawDevelopment && loader.typeOfActiveMedia('raw')) await vm.rawDevelopment.applyData(candidate.rawDevelopment);
            await window.lutify.applyCompare(candidate.blocks, "waitToLoad", false);

            this.applying = false;
            this.$store.dispatch("Loader/showProgressBar", false);

            window.UI.removeFileLoadingSpinner(candidate.id);
            // UI.removeCandidateLoadingSpinner();
            window.UI.hideImageLoader();
            window.UI.toggleImageTools();
        },

        async remove() {
            await candidatesStore.removeItem(this.candidate.id);
            this.$store.commit('UI/REMOVE_CANDIDATE', this.candidate.id);
        },

        startFileRenaming(file) {
            this.$store.dispatch("Projects/setAction", {
                type: "file",
                action: "file-rename",
                data: file,
            });

            this.renamingFile = file.id;
            this.renamingFileName = this.stripTimestampAndExtension(file.name);
        },

        async renameFile(e) {
            if (e.key == "Enter" || e.keyCode == 13) {
                this.renamingFile = false;

                let oldFilename = this.$store.state.Projects.action.data.name;
                let fileType = oldFilename.substring(oldFilename.lastIndexOf(".") + 1);
                let newFilename = `${this.renamingFileName}.${Date.now()}.${fileType}`;

                if (this.stripTimestampAndExtension(oldFilename) !== this.renamingFileName) {
                    await this.$store.dispatch("UI/renameCandidate", {
                        id: this.$store.state.Projects.action.data.id,
                        name: newFilename,
                        nicename: this.stripTimestampAndExtension(newFilename),
                    });

                    let candidateStoreData = await candidatesStore.getItem(this.$store.state.Projects.action.data.id);
                    if (candidateStoreData)
                        candidatesStore.setItem(candidateStoreData.id, {
                            ...candidateStoreData,
                            name: newFilename,
                            nicename: this.renamingFileName
                        });
                }

                this.endFileRenaming();
            } else if (e.key == "Escape" || e.keyCode == 27) {
                this.endFileRenaming();
            }
        },

        endFileRenaming() {
            this.renamingFile = false;
            this.$store.dispatch("Projects/setAction", {
                type: "file",
                action: "file-rename-end",
                data: null,
            });
        },

        async downloadLUT(candidate) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let candidateStoreData = await window.lutify.fetchCandidate(vm.$store.getters["UI/candidateById"](candidate.id));

            await this.$store.dispatch("Projects/setAction", {
                type: "candidate",
                action: "export-lut-from-asset",
                data: [candidateStoreData],
            });

            UI.showModal(UI.modals.exportLut.el);
            document.querySelector('#modal-export-lut [name="export-lut-name"]').value = candidate.nicename;
        },

        async downloadImage(candidate) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            // let matchStoreData = await window.lutify.fetchMatch(vm.$store.getters["Projects/matchById"](match.id));
            let storeData = await window.lutify.fetchCandidate(candidate);
            download(this.candidate.image, `${storeData.nicename}.jpg`, "image/jpeg");
        },
    },
};
</script>

<style lang="scss">
.candidate-container {
    display: grid;
    grid-template-areas: "image image" "label delete-btn";
    grid-template-columns: 1fr 20px;
    grid-row-gap: 5px;
    align-items: center;
    align-content: center;

    &:not(:last-of-type) {
        margin-bottom: 20px;
    }

    > img {
        width: 100%;
        grid-area: image;
        cursor: pointer;
        border: 2px solid transparent;
        border-radius: var(--thumbnail-lg-border-radius);

        &:hover {
            border: 2px solid var(--primary);
        }
    }

    &.selected > img {
        border: 2px solid var(--primary);
    }

    > .label-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        grid-area: label;
        overflow: hidden;

        .label-container-inner {
            display: grid;
            align-content: center;
            grid-template-columns: 1fr minmax(0, auto);
            width: 100%;
        }

        .name {
            color: var(--font-default-color);
            font-size: 0.9rem;
            text-align: left;
            line-height: 1.2;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            align-self: center;
            padding-right: 5px;
        }

        > .v-progress-circular {
            margin-left: 10px;
            margin-right: 5px;
        }

        .v-input {
            font-size: 12px;

            .v-input__control {
                min-height: initial;
                // height: 10px;
                input {
                    line-height: 1;
                    color: var(--font-default-color) !important;
                    padding: 0;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                }

                .v-input__slot {
                    padding: 0;
                    background: transparent;
                }
            }

            &--is-focused {
                .v-input__slot {
                    // background: #363636;
                    background: transparent;
                }
            }
        }
    }

    i.material-icons {
        font-size: 18px;
        cursor: pointer;
    }
}
</style>
